import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import BrushedBarChart from 'components/BrushedBarChart';
import { ROUTE_CONSTANTS } from 'constants/route';
import { SEARCH_QUERY_PARAMS } from 'constants/querystring';

const textShadow = { textShadow: '2px 2px 5px rgba(0,0,0,0.5)' };

const sharedPanel = {
  color: '#fff',
  fontSize: '6rem',
  zIndex: '1',
  width: '200px',
  height: '200px',
  pointerEvents: 'none',
  ...textShadow,
};

const svgLineStyle = ` style='filter: drop-shadow(rgba(0, 0, 0, 0.5) 2px 2px 5px);'`;

const useStyles = makeStyles((theme) => ({
  root: {
    height: 'calc(100vh - 100px)',
    position: 'relative',
    overflow: 'hidden',
    background: '#ffffff',
    backgroundImage: 'url(vicksburg-ms.jpg)',
    backgroundSize: 'cover',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  background: {
    position: 'absolute',
    opacity: (props) => (props.chartHovered ? 0.6 : 0.2),
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    backgroundColor: '#000',
    transition: 'opacity 0.5s ease-in-out',
    zIndex: 0,
  },
  chartContainer: {
    display: 'inline-block',
    position: 'relative',
    zIndex: 10,
    '&:hover .leftPanel': {
      left: '-200%',
      transition: 'left 0.5s ease-in-out',
    },
    '&:hover .rightPanel': {
      right: '-200%',
      transition: 'right 0.5s ease-in-out',
    },
    '& #BrushedBarChart .xAxis line, & #BrushedBarChart .xAxis path, & #BrushedBarChart .yAxis, & #BrushedBarChart .mini':
      {
        opacity: 0,
        transition: 'opacity 0.5s ease-in-out',
      },
    '&:hover #BrushedBarChart .xAxis line, &:hover #BrushedBarChart .xAxis path, &:hover #BrushedBarChart .yAxis, &:hover #BrushedBarChart .mini':
      {
        opacity: 1,
        transition: 'opacity 0.5s ease-in-out',
      },
    '& #BrushedBarChart text': {
      fill: '#fff',
    },
    '& #BrushedBarChart rect.bar:last-child': {
      fill: '#20a7db !important',
      transition: 'fill 0.5s ease-in-out',
    },
    '&:hover #BrushedBarChart rect.bar:last-child': {
      fill: '#fff !important',
      transition: 'fill 0.5s ease-in-out',
    },
    '& #BrushedBarChart text.bar-steve': {
      opacity: '1 !important',
      transition: 'opacity 0.5s ease-in-out',
    },
    '&:hover #BrushedBarChart text.bar-steve': {
      opacity: '0 !important',
      transition: 'opacity 0.5s ease-in-out',
    },
  },
  title: {
    position: 'absolute',
    top: '-40px',
    width: '100%',
    display: 'block',
    fontSize: '2rem',
    textAlign: 'center',
    color: '#fff',
    fontFamily: '"Roboto Slab", "Helvetica", "Arial", sans-serif',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.75rem',
      top: '-25px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.25rem',
      top: '-25px',
    },
    ...textShadow,
  },
  titleQuarter: {
    position: 'absolute',
    top: '-60px',
    width: '100%',
    display: 'block',
    fontSize: '1.75rem',
    textAlign: 'center',
    color: '#fff',
    fontFamily: '"Roboto Slab", "Helvetica", "Arial", sans-serif',
    ...textShadow,
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  leftPanel: {
    position: 'absolute',
    top: '-80px',
    left: '-75px',
    transition: 'left 0.5s ease-in-out',
    background: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><path d='M0 99 L99 0 L100 1 L1 100' fill='white' ${svgLineStyle} /></svg>")`,
    ...sharedPanel,
    '& div': {
      position: 'absolute',
      top: '-20px',
      left: '-40px',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  rightPanel: {
    position: 'absolute',
    right: '-75px',
    bottom: '-55px',
    transition: 'right 0.5s ease-in-out',
    background: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><path d='M0 99 L99 0 L100 1 L1 100' fill='white' ${svgLineStyle} /></svg>")`,
    ...sharedPanel,
    '& div': {
      position: 'absolute',
      bottom: '0',
      right: '-40px',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}));

const getYearAndQuarter = (quarterYear) => {
  if (quarterYear.length < 7) {
    return {
      year: '',
      quarter: '',
    };
  }

  const year = quarterYear.substring(0, 4);
  const quarter = quarterYear.substring(5);
  return { year, quarter };
};

const HeroPremiumLineGraph = ({ data = [] }) => {
  const [chartHovered, setChartHovered] = useState(false);
  const [quarter, setQuarter] = useState(null);
  const [year, setYear] = useState(null);
  const navigate = useNavigate();

  const handleItemClick = useCallback(() => {
    const { year: endYear, quarter: endQuarter } = getYearAndQuarter(
      data[data.length - 1].quarterYear,
    );
    const { year: startYear, quarter: startQuarter } = getYearAndQuarter(
      data[data.length - 4].quarterYear,
    );

    const removeQ = (quarter) => (quarter ? quarter.replace('Q', '') : quarter);

    navigate(
      `${ROUTE_CONSTANTS.REPORT_PREMIUM_TREND}/?${SEARCH_QUERY_PARAMS.StartYear}=${startYear}&${
        SEARCH_QUERY_PARAMS.StartQuarter
      }=${removeQ(startQuarter)}&${SEARCH_QUERY_PARAMS.EndYear}=${endYear}&${
        SEARCH_QUERY_PARAMS.EndQuarter
      }=${removeQ(endQuarter)}`,
    );
  }, [navigate, data]);

  useEffect(() => {
    if (data.length > 0) {
      const latest = data[data.length - 1];
      const { year, quarter } = getYearAndQuarter(latest.quarterYear);
      setQuarter(quarter);
      setYear(year.slice(-2)); // Make this a two-digit year for display
    }
  }, [data]);

  const classes = useStyles({ chartHovered });

  return (
    <>
      <div className={classes.root}>
        <div className={`${classes.background} background`}></div>
        <div
          className={`${classes.chartContainer} chartContainer`}
          onMouseOver={() => setChartHovered(true)}
          onMouseOut={() => setChartHovered(false)}
        >
          <div className={classes.titleQuarter}>
            {quarter} 20{year}
          </div>
          <div className={`${classes.title} title`}>Quarterly Premium Trend</div>
          <div className={`${classes.leftPanel} leftPanel`}>
            <div>{quarter ? quarter : ''}</div>
          </div>
          <div className={`${classes.rightPanel} rightPanel`}>
            <div>{year ? year : ''}</div>
          </div>
          <BrushedBarChart
            data={data}
            onItemClick={handleItemClick}
            idKey="quarterYear"
            valueKey="premium"
          />
        </div>
      </div>
    </>
  );
};

export default HeroPremiumLineGraph;
